// Read-only ICS subscriptions (REQ-CAL-009) — parse an iCalendar feed (Howick Intermediate, NZ
// public holidays, the MoE school-terms calendar) into events, then map them to source-tagged
// read-only `item` rows rendered via the 003 external seam (REQ-VIEW-007). Pure: no network, no DB;
// the Edge Function fetches the feed and upserts. No writeback — these never sync back out.

import type { Term } from "./planner";

export interface IcsEvent {
  uid: string;
  summary: string;
  start: string; // "YYYY-MM-DD" (all-day) or an ISO instant
  end: string | null;
  allDay: boolean;
}

/** Undo RFC 5545 text escaping (\\ \; \, \n) in a value. */
function unescapeIcs(v: string): string {
  return v.replace(/\\n/gi, "\n").replace(/\\([;,\\])/g, "$1");
}

/** A DTSTART/DTEND value → a canonical date/instant + whether it's all-day. */
function parseIcsDate(val: string, isDate: boolean): { value: string; allDay: boolean } {
  if (isDate || /^\d{8}$/.test(val)) {
    const d = `${val.slice(0, 4)}-${val.slice(4, 6)}-${val.slice(6, 8)}`;
    return { value: d, allDay: true };
  }
  // basic datetime 20260126T090000Z → ISO
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/.exec(val);
  if (m) {
    const [, y, mo, da, h, mi, s, z] = m;
    return { value: `${y}-${mo}-${da}T${h}:${mi}:${s}${z ? "Z" : ""}`, allDay: false };
  }
  return { value: val, allDay: false };
}

/** Parse an ICS feed into events. Handles RFC 5545 line folding, `;PARAM` on keys, VALUE=DATE. */
export function parseIcs(text: string): IcsEvent[] {
  const unfolded = text.replace(/\r?\n[ \t]/g, ""); // unfold continuation lines
  const events: IcsEvent[] = [];
  let cur: Partial<IcsEvent> | null = null;
  for (const line of unfolded.split(/\r?\n/)) {
    if (line.startsWith("BEGIN:VEVENT")) {
      cur = {};
      continue;
    }
    if (line.startsWith("END:VEVENT")) {
      if (cur?.uid && cur.summary && cur.start) {
        events.push({ uid: cur.uid, summary: cur.summary, start: cur.start, end: cur.end ?? null, allDay: cur.allDay ?? false });
      }
      cur = null;
      continue;
    }
    if (!cur) continue;
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const rawKey = line.slice(0, colon);
    const val = line.slice(colon + 1).trim();
    const key = rawKey.split(";")[0].toUpperCase();
    const isDate = /VALUE=DATE\b/i.test(rawKey);
    if (key === "UID") cur.uid = val;
    else if (key === "SUMMARY") cur.summary = unescapeIcs(val);
    else if (key === "DTSTART") {
      const p = parseIcsDate(val, isDate);
      cur.start = p.value;
      cur.allDay = p.allDay;
    } else if (key === "DTEND") {
      cur.end = parseIcsDate(val, isDate).value;
    }
  }
  return events;
}

// ── Map an ICS event → a read-only `item` (REQ-CAL-009 / REQ-VIEW-007) ─────────────────────────
export interface IcsItemFields {
  type: "event";
  title: string;
  due_at: string; // the start, as an instant (all-day → UTC midnight)
  source: string; // "external:<feed>" → read-only + source badge, never writes back
  gcal_event_id: string; // the ICS UID reused as the external event id → idempotent upsert
  status: "open";
}

/** The instant for an ICS start (all-day → that date at UTC midnight). */
export function icsDueAt(ev: IcsEvent): string {
  return ev.allDay ? `${ev.start}T00:00:00Z` : new Date(ev.start).toISOString();
}

/** Map one ICS event to a source-tagged read-only item. `feed` is the external source key. */
export function icsEventToItem(ev: IcsEvent, feed: string): IcsItemFields {
  return {
    type: "event",
    title: ev.summary,
    due_at: icsDueAt(ev),
    source: `external:${feed}`,
    gcal_event_id: ev.uid,
    status: "open",
  };
}

// ── Context vs actionable (REQ-CAL-010) — a term-structure marker is CONTEXT (a span), a real
// event is an actionable/read-only event. A term-dates band must not become four to-dos. ──────
/** Classify an ICS event: "term" markers (start/end term, earliest/latest term start) drive the
 *  term spans (context); everything else is a normal "event" (a read-only item via REQ-CAL-009). */
export function classifyIcs(ev: IcsEvent): "term" | "event" {
  return /\bterm\s*\d\b|\bstart\s*term\b|\bend\s*(?:of\s*)?term\b|\bterm\s*\d?\s*start\b/i.test(ev.summary)
    ? "term"
    : "event";
}

/** Derive the school **term spans** (context) from the MoE feed's markers — pairing each term's
 *  start (Term 1: its latest start date; Terms 2–4: "Start term N") with its "End of term N".
 *  Feeds termContext/termWeek (REQ-VIEW-017). Pure. */
export function deriveTerms(events: IcsEvent[]): Term[] {
  const starts = new Map<number, string>();
  const ends = new Map<number, string>();
  for (const e of events) {
    if (classifyIcs(e) !== "term") continue;
    const s = e.summary.toLowerCase();
    const end = s.match(/end\s*(?:of\s*)?term\s*(\d)/) || s.match(/term\s*(\d)\s*end/); // "End of term N" or "term N end date"
    const start = s.match(/start\s*term\s*(\d)/) || s.match(/term\s*(\d)\s*start/);
    if (end) ends.set(Number(end[1]), e.start);
    else if (/latest\s*term\s*1\s*start/.test(s)) starts.set(1, e.start); // Term 1: use the latest start
    else if (start) starts.set(Number(start[1]), e.start);
  }
  const terms: Term[] = [];
  for (const t of [1, 2, 3, 4]) {
    const start = starts.get(t);
    const end = ends.get(t);
    if (start && end) terms.push({ term: t, start, end });
  }
  return terms;
}
